import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { casesApi } from './caseApi';
import { UserContext } from '../../context/UserContext';
import LoadingSubmitButton from '../../components/LoadingSubmitButton';
import { useCases } from './CasesContext';
import TagsInput from '../../components/TagsInput';
import InputWithIcon from '../../components/InputWithIcon';
import DropdownWithIcon from '../../components/DropdownWithIcon';
import FileUpload from '../../features/trainings/components/FileUpload';
import protocolIcon from '../../assets/protocols/protocol.svg';
import tagsIcon from '../../assets/protocols/tags.svg';
import summaryIcon from '../../assets/protocols/summary.svg';
import hospitalIcon from '../../assets/protocols/hospital.svg';
import DeleteConfirmationDialog from '../../components/dialogs/DeleteConfirmationDialog';
import UploadErrors from '../../components/UploadErrors';
import './AddEditMedicalCase.scss';
import { Skeleton } from '../../components/Loaders/Skeleton/Skeleton';
import PageTitle from '../../components/PageTitle';
import { cancelButton, confirmButton, pageActionsContainer } from '../../components/Buttons';

const AddEditMedicalCase = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // Get location object
  const preloadedFileProcessedRef = useRef(false); // Ref to track if preloaded file was processed
  const nameInputRef = useRef(null); // Ref for the name input field
  const hospitalSelectRef = useRef(null); // Ref for the hospital select field
  const departmentSelectRef = useRef(null); // Ref for the department select field

  // Get user, hospitals list, and initialization status from context
  const { user, hospitals: contextHospitals, isInitialized } = useContext(UserContext);
  const { refreshCases } = useCases(); // Get refreshCases from context
  const isEditMode = !!id;

  // State for form fields
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tags: [],
    files: [], // This will now hold only NEWLY added files
  });
  const [existingFiles, setExistingFiles] = useState([]); // State for files loaded in edit mode
  const [filesToDelete, setFilesToDelete] = useState([]); // State for existing files marked for deletion
  // Initialize hospital ID state to empty
  const [selectedHospitalId, setSelectedHospitalId] = useState('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  // Re-introduce state for dropdown visibility
  const [showHospitalDropdown, setShowHospitalDropdown] = useState(false);
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const [availableDepartments, setAvailableDepartments] = useState([]);


  // Other states
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(!!isEditMode); // Renamed from loading
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null); // Load-time error, shown at the top of the page
  // Save failures get their own state so they can be rendered next to the save
  // button — the top-of-page banner is off-screen by the time the user submits.
  const [saveError, setSaveError] = useState(null);
  const [nameError, setNameError] = useState(null); // Specific error for name field
  const [hospitalError, setHospitalError] = useState(null); // Specific error for hospital field
  const [departmentError, setDepartmentError] = useState(null); // Specific error for department field
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false); // State for confirmation dialog
  const [isConfirmedNoPatientInfo, setIsConfirmedNoPatientInfo] = useState(false); // New state for the confirmation checkbox
  const [confirmationError, setConfirmationError] = useState(null); // New state for confirmation checkbox error


  // Use the main hospital list from context for dropdown options
  const availableHospitalsForDropdown = contextHospitals || [];

  // Effect Hook for main initialization and edit mode fetching
  // This effect handles:
  // - Initializing the form for creating a new case or editing an existing one.
  // - Processing a preloaded file passed via navigation state (e.g., from mobile photo capture).
  //   This includes image compression and setting the file in the form state.
  // - Ensuring that preloaded file processing and initial form setup for CREATE mode
  //   happen only once, even with React StrictMode's double effect invocation in development
  //   or effect re-runs due to dependency changes (like navigation clearing location.state).
  useEffect(() => {
    const initializePage = async () => {
      // Helper to process a preloaded file from location.state.
      // FileUpload will handle compression, so we just pass the file as-is.
      const processPreloadedFileInternal = async () => {
        // This check ensures that we only attempt to process a file if it exists in location.state,
        // we are in CREATE mode, and this specific preloaded file hasn't been processed yet.
        if (location.state?.uploadedFile && !isEditMode && !preloadedFileProcessedRef.current) {
          const file = location.state.uploadedFile;
          // Mark this preloaded file as processed to prevent re-processing on subsequent effect runs.
          preloadedFileProcessedRef.current = true;
          // Clear the uploadedFile from navigation state to prevent reprocessing if the user navigates back and forth.
          navigate(location.pathname, { replace: true, state: {} }); 
          return file;
        }
        // No file in location.state, or it's edit mode, or the file was already processed.
        return null;
      };

      // Only proceed if context is initialized
      if (!isInitialized) {
        setIsLoadingInitialData(true);
        setSelectedHospitalId('');
        setShowHospitalDropdown(false);
        return;
      }

      // Determine dropdown visibility and default hospital based on user roles
      const userRoles = user?.roles || [];
      const isSuperAdmin = user?.isSuperAdmin || false;
      
      const associatedHospitalIds = userRoles.map(role => role.hospital).filter(Boolean);
      const uniqueHospitalIds = [...new Set(associatedHospitalIds)];
      const shouldShowHospitalDropdown = (isSuperAdmin && contextHospitals.length > 1) || (!isSuperAdmin && uniqueHospitalIds.length > 1);
      setShowHospitalDropdown(shouldShowHospitalDropdown);

      const associatedDepartmentIds = userRoles.map(role => role.department).filter(Boolean);
      const uniqueDepartmentIds = [...new Set(associatedDepartmentIds)];
      const shouldShowDepartmentDropdown = (isSuperAdmin && contextHospitals.length > 0) || (!isSuperAdmin && uniqueDepartmentIds.length > 1);
      setShowDepartmentDropdown(shouldShowDepartmentDropdown);


      if (isEditMode) {
        // --- EDIT MODE ---
        setIsLoadingInitialData(true);
        setError(null);
        setSelectedHospitalId(''); // Reset while loading
        try {
          const caseData = await casesApi.get(id);
          if (caseData) {
            setFormData({
              name: caseData.name || '',
              description: caseData.description || '',
              tags: caseData.tags || [],
              files: [], // Edit mode starts with no new files
            });
            const fetchedHospitalId = caseData.hospital?._id || caseData.hospital || '';
            setSelectedHospitalId(fetchedHospitalId);
            const fetchedDepartmentId = caseData.department?._id || caseData.department || '';
            setSelectedDepartmentId(fetchedDepartmentId);
            setExistingFiles(caseData.files?.map(f => ({ url: f.url, name: f.name || f.url.split('/').pop(), contentType: f.contentType })) || []);
            setFilesToDelete([]);
            setIsConfirmedNoPatientInfo(true);
          } else {
            setError('המקרה לא נמצא');
          }
        } catch (err) {
          setError('אירעה שגיאה בטעינת המקרה');
          console.error(err);
        } finally {
          setIsLoadingInitialData(false);
        }
      } else {
        // --- CREATE MODE ---
        setIsLoadingInitialData(false);
        setError(null);
        setIsConfirmedNoPatientInfo(false);

        // The preloadedFileProcessedRef ensures that file processing and the initial
        // setFormData for CREATE mode occur only once.
        if (!preloadedFileProcessedRef.current) {
          // Attempt to process a preloaded file. This function will set
          // preloadedFileProcessedRef.current = true if it successfully processes a file.
          const fileFromPreload = await processPreloadedFileInternal();
          
          // Initialize the form. If a file was preloaded and processed, include it.
          // This block runs only if preloadedFileProcessedRef was false at the start of this if-block,
          // meaning this is the definitive first setup for the create mode instance.
          setFormData({
            name: '',
            description: '',
            tags: [],
            files: fileFromPreload ? [fileFromPreload] : []
          });
        }
        // If preloadedFileProcessedRef.current is true at the start of this `else` block,
        // it means the preloaded file (if any) and initial form fields have already been set.
        // Subsequent runs of this useEffect (e.g., due to navigate clearing location.state,
        // or StrictMode re-mounts) will skip the above block, preserving formData.
        
        // Set hospital ID based on user roles (common for create mode).
        // This can be set on each run for CREATE mode as it's not user-editable field directly tied to this specific logic.
        if (user) {
          if (!shouldShowHospitalDropdown && uniqueHospitalIds.length === 1) {
            setSelectedHospitalId(uniqueHospitalIds[0]);
          } else {
            setSelectedHospitalId('');
          }
          if (!shouldShowDepartmentDropdown && uniqueDepartmentIds.length === 1) {
            setSelectedDepartmentId(uniqueDepartmentIds[0]);
          } else {
            setSelectedDepartmentId('');
          }
        } else {
          setSelectedHospitalId('');
          setSelectedDepartmentId('');
        }
      }
    };

    initializePage();
    // No cleanup needed here for createModeFormInitializedRef as it's removed.
    // preloadedFileProcessedRef handles the one-time nature of processing a specific preloaded file.
  }, [user, id, isEditMode, isInitialized, contextHospitals, navigate, location.pathname, location.state]);

  useEffect(() => {
    if (selectedHospitalId) {
      const hospital = contextHospitals.find(h => h._id === selectedHospitalId);
      let departments = [];

      if (user.isSuperAdmin) {
        departments = hospital?.departments || [];
      } else {
        const userDepartmentsForHospital = user.roles
          .filter(role => role.hospital === selectedHospitalId)
          .map(role => role.department);
        
        const uniqueDepartments = [...new Set(userDepartmentsForHospital)];
        
        departments = hospital?.departments.filter(d => uniqueDepartments.includes(d._id)) || [];
      }

      setAvailableDepartments(departments);

      setSelectedDepartmentId(prev => {
        // If the current selected department is in the new list, keep it (e.g. initial load)
        if (prev && departments.some(d => d._id === prev)) {
          return prev;
        }
        
        // Auto-select if regular user has exactly one department available
        if (!user.isSuperAdmin && departments.length === 1) {
          return departments[0]._id;
        }
        
        return '';
      });
    } else {
      setAvailableDepartments([]);
      // Reset department selection when no hospital is selected
      setSelectedDepartmentId('');
    }
  }, [selectedHospitalId, contextHospitals, user]);

  // Handler for FileUpload component - separates new files from existing ones
  const handleFilesChange = (newFiles) => {
    const newUploadedFiles = newFiles.filter(file => !file.isInitial);
    const existingFilesFromUpload = newFiles.filter(file => file.isInitial && file.originalFile);
    
    // Find deleted existing files
    const currentExistingUrls = existingFilesFromUpload.map(f => f.originalFile.url);
    const originalExistingUrls = existingFiles.map(f => f.url);
    const deletedFiles = originalExistingUrls.filter(url => !currentExistingUrls.includes(url));
    
    setFormData(prev => ({
      ...prev,
      files: newUploadedFiles
    }));
    
    setExistingFiles(existingFilesFromUpload.map(file => file.originalFile));
    setFilesToDelete(prev => [...prev, ...deletedFiles]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setNameError(null);
    setHospitalError(null);
    setDepartmentError(null);
    setConfirmationError(null); // Clear confirmation error
    setError(null); // Clear general errors as well
    setSaveError(null);

    // 1. Validate Name
    if (!formData.name.trim()) {
      setNameError('שדה חובה');
      nameInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setSaving(false);
      return;
    }

    // 2. Validate Hospital (only if dropdown is shown)
    if (showHospitalDropdown && !selectedHospitalId) {
      setHospitalError('שדה חובה');
      hospitalSelectRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setSaving(false);
      return;
    }

    // 3. Validate Department (only if dropdown is shown)
    if (showDepartmentDropdown && !selectedDepartmentId) {
      setDepartmentError('שדה חובה');
      departmentSelectRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setSaving(false);
      return;
    }

    // 4. Validate Confirmation Checkbox
    if (!isConfirmedNoPatientInfo) {
      setConfirmationError('חובה לאשר');
      // Potentially scroll to the checkbox if it's off-screen, though it's close to the save button.
      // For now, just setting the error.
      setSaving(false);
      return;
    }
    
    // 5. File Confirmation (was 4)
    if (existingFiles.length === 0 && formData.files.length === 0) {
      setIsConfirmDialogOpen(true); // Open custom dialog
      // Don't return here; let the dialog handle the next step
      // setSaving(false) will be handled by dialog actions or if validation fails before this
      return; // Stop handleSubmit for now, will be resumed by dialog's onConfirm
    }

    // If file check passes (or not applicable), proceed to actual save operation
    await proceedWithSave();
  };
  
  // Extracted save logic to be called after confirmation if needed
  const proceedWithSave = async () => {
    // Ensure saving is true if we reach here directly or via confirmation
    if (!saving) setSaving(true); 
    
    try {
      // Ensure errors are cleared again in case this is called from confirmation
      setNameError(null);
      setHospitalError(null);
      setDepartmentError(null);
      setError(null);
      setSaveError(null);

      // Re-validate name and hospital before proceeding, in case state changed while dialog was open
      // This is a safeguard, ideally state wouldn't change, but good practice.
      if (!formData.name.trim()) {
        setNameError('שדה חובה');
        nameInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setSaving(false);
        return;
      }
      if (showHospitalDropdown && !selectedHospitalId) {
        setHospitalError('שדה חובה');
        hospitalSelectRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setSaving(false);
        return;
      }
      if (showDepartmentDropdown && !selectedDepartmentId) {
        setDepartmentError('שדה חובה');
        departmentSelectRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setSaving(false);
        return;
      }

      if (isEditMode) {
        // Prepare payload for update, including files to delete and new files
        const updatePayload = {
          name: formData.name.trim(),
          description: (formData.description || '').trim(),
          tags: formData.tags,
          hospital: selectedHospitalId,
          department: selectedDepartmentId,
          deletedFiles: filesToDelete, // Send the URLs/identifiers of files to delete
          newFiles: formData.files,     // Send only the newly added files
        };
        await casesApi.update(id, updatePayload);
      } else {
        // Prepare payload for create
        const createPayload = {
          name: formData.name.trim(),
          description: (formData.description || '').trim(),
          tags: formData.tags,
          hospital: selectedHospitalId,
          department: selectedDepartmentId,
          files: formData.files, // Send all files for creation
        };
        // console.log('CaseEditor handleSubmit - createPayload:', JSON.stringify(createPayload, null, 2));
        // console.log('CaseEditor handleSubmit - createPayload.files:', createPayload.files);
        // if (createPayload.files && createPayload.files.length > 0) {
        //   createPayload.files.forEach((file, index) => {
        //     console.log(`File ${index}: name=${file.name}, size=${file.size}, type=${file.type}, lastModified=${file.lastModified}`);
        //   });
        // }
        await casesApi.create(createPayload);
      }

      // Refresh the cases list in the context before navigating
      refreshCases();

      // Navigate back to cases list on success
      navigate('/cases');
    } catch (err) {
      console.error(err);
      // Prefer the server's Hebrew reason ("סוג קובץ לא נתמך", "בעיה בהעלאת
      // הקובץ") over a generic message — it is usually the only clue about
      // which attachment blocked the save.
      setSaveError(err?.serverError?.hebMessage || 'אירעה שגיאה בשמירת המקרה. אנא נסה שנית.');
    } finally {
      setSaving(false);
      setIsConfirmDialogOpen(false); // Ensure dialog is closed on finish/error
    }
  };

  const handleConfirmNoFiles = () => {
    setIsConfirmDialogOpen(false);
    proceedWithSave(); // Call the extracted save logic
  };

  const handleCancelNoFiles = () => {
    setIsConfirmDialogOpen(false);
    setSaving(false); // Ensure saving is stopped
  };

  // Handler for the TagsInput component
  const handleTagsChange = (newTags) => {
    setFormData(prev => ({ ...prev, tags: newTags }));
  };

  // Show loader if context is not initialized OR if fetching case data in edit mode
  // This condition determines if we show a full page loader or the form.
  if (!isInitialized|| (isLoadingInitialData && isEditMode)) {
    return (
      <div className="case-editor-container loading-container">
       <Skeleton>
          <Skeleton.Text />
          <Skeleton.Block height='50px' />
          <Skeleton.Block height='50px' />
          <Skeleton.Block height='200px' />
          <Skeleton.Block height='50px' />
          <Skeleton.Block height='200px' />
       </Skeleton>
      </div>
    );
  }

  // --- Render Form only after initialization ---
  // If we reach here, isInitialized is true.
  // The Loader component will overlay the form if isLoadingInitialData is true in editMode.
  return (
      <div className="case-editor-container">
        {/* <Loader isLoading={true} /> */}
        <PageTitle title={isEditMode ? 'עריכת מקרה' : 'מקרה חדש'} />

        {error && <div className="form-error">{error}</div>}

        <DeleteConfirmationDialog
          open={isConfirmDialogOpen}
          title="לא צורף אף קובץ. האם אתה בטוח שברצונך לשמור את המקרה?"
          onConfirm={handleConfirmNoFiles}
          onClose={handleCancelNoFiles}
        />

        <form onSubmit={handleSubmit}>
          {/* Changed label and field to 'name' */}
          <div className="form-group">
            <InputWithIcon
              icon={protocolIcon}
              placeholder="שם המקרה"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (nameError) setNameError(null);
              }}
              ref={nameInputRef}
            />
            {nameError && <div className="form-field-error" style={{ color: 'red', fontSize: '0.875em', marginTop: '0.25rem' }}>{nameError}</div>}
          </div>

          {/* Conditionally render Hospital Dropdown based on state variable */}
          {showHospitalDropdown && (
            <div className="form-group">
              <DropdownWithIcon
                icon={hospitalIcon}
                placeholder="בית חולים"
                required
                value={selectedHospitalId}
                onChange={(e) => {
                  setSelectedHospitalId(e.target.value);
                  if (hospitalError) setHospitalError(null);
                }}
                ref={hospitalSelectRef}
              >
                {/* Map over the available hospitals from context */}
                {availableHospitalsForDropdown.map((hospital) => (
                  <option key={hospital._id} value={hospital._id}>
                    {hospital.nameHebrew || hospital.nameEnglish}
                  </option>
                ))}
              </DropdownWithIcon>
              {hospitalError && <div className="form-field-error" style={{ color: 'red', fontSize: '0.875em', marginTop: '0.25rem' }}>{hospitalError}</div>}
            </div>
          )}
          {/* End Hospital Dropdown */}

          {/* Conditionally render Department Dropdown based on state variable */}
          {showDepartmentDropdown && (
            <div className="form-group">
              <DropdownWithIcon
                icon={hospitalIcon}
                placeholder="מחלקה"
                required
                value={selectedDepartmentId}
                onChange={(e) => {
                  setSelectedDepartmentId(e.target.value);
                  if (departmentError) setDepartmentError(null);
                }}
                ref={departmentSelectRef}
              >
                {/* Map over the available departments */}
                {availableDepartments.map((department) => (
                  <option key={department._id} value={department._id}>
                    {department.nameHebrew || department.nameEnglish}
                  </option>
                ))}
              </DropdownWithIcon>
              {departmentError && <div className="form-field-error" style={{ color: 'red', fontSize: '0.875em', marginTop: '0.25rem' }}>{departmentError}</div>}
            </div>
          )}
          {/* End Department Dropdown */}
          <div className="form-group">
            <TagsInput
              tags={formData.tags}
              onChange={handleTagsChange}
              label="תגיות" // Keep the label consistent
              placeholder="הקלד תגית ולחץ אנטר..." // Adjust placeholder if needed
              icon={tagsIcon}
            />
          </div>

          {/* Restore Description Field */}
          <div className="form-group">
            <InputWithIcon
              icon={summaryIcon}
              placeholder="תיאור מקרה"
              multiline
              rows={5}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
          </div>

          <div className="form-group">
            <FileUpload
              onFilesChange={handleFilesChange}
              initialFiles={[
                ...existingFiles.map(f => ({
                  ...f,
                  isInitial: true,
                  originalFile: f,
                  name: f.name,
                  preview: f.url
                })),
                ...formData.files
              ]}
              maxFiles={50}
              maxSize={20 * 1024 * 1024}
              // Must stay within the server whitelist in config/multer.js —
              // `image/*` / `video/*` let the picker offer .heic, .webm and
              // friends, which the server then rejects, failing the whole save
              // rather than just that file.
              accept={{
                'application/pdf': ['.pdf'],
                'application/msword': ['.doc'],
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                'application/vnd.ms-excel': ['.xls'],
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                'application/vnd.ms-powerpoint': ['.ppt'],
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
                'application/rtf': ['.rtf'],
                'text/plain': ['.txt'],
                'text/csv': ['.csv'],
                'message/rfc822': ['.eml'],
                'application/vnd.ms-outlook': ['.msg'],
                'image/png': ['.png'],
                'image/jpeg': ['.jpg', '.jpeg'],
                'image/webp': ['.webp'],
                'image/gif': ['.gif'],
                'image/bmp': ['.bmp'],
                'image/tiff': ['.tif', '.tiff'],
                'video/mp4': ['.mp4'],
                'video/quicktime': ['.mov'],
                'video/x-msvideo': ['.avi'],
              }}
              compressImages={true}
              compressionThreshold={250 * 1024}
              compressionOptions={{
                maxSizeMB: 0.1,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                initialQuality: 0.7,
                alwaysKeepResolution: false,
                fileType: 'image/jpeg',
              }}
              dropzoneText="בחר קבצים או גרור לכאן"
              dropzoneSubtext="תמונות (PNG, JPG, WEBP, GIF, BMP, TIF), וידאו (MP4, MOV, AVI), מסמכים (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, RTF), דואר (EML, MSG) - עד 20MB לקובץ"
              enableFileNameEdit={false}
            />
          </div>

          {/* Confirmation Checkbox */}
          <div className="form-group">
            <div className="confirmation-checkbox-container">
              <input
                type="checkbox"
                id="noPatientInfoConfirm"
                checked={isConfirmedNoPatientInfo}
                onChange={(e) => {
                  setIsConfirmedNoPatientInfo(e.target.checked);
                  if (confirmationError) setConfirmationError(null);
                }}
                className="form-checkbox" style={{marginLeft: '5px'}}
              />
              <label htmlFor="noPatientInfoConfirm" className="form-checkbox-label">
                המידע המוזן לא מכיל מידע של מטופל
              </label>
            </div>
            {confirmationError && <div className="form-field-error" style={{ color: 'red', fontSize: '0.875em', marginTop: '0.25rem' }}>{confirmationError}</div>}
          </div>

          {/* autoDismissMs=0: a failed save must stay until the user acts. */}
          <UploadErrors
            messages={saveError ? [saveError] : []}
            onDismiss={() => setSaveError(null)}
            autoDismissMs={0}
          />

          <div style={pageActionsContainer}>
            <LoadingSubmitButton
              type="submit"
              style={confirmButton}
              isLoading={saving}
              text="שמור"
              loadingText="שומר..."
              disabled={saving} // Changed: Save button is only disabled when saving
            />
            <button
              type="button"
              style={cancelButton}
              onClick={() => navigate('/cases')}
              disabled={saving}
            >
              ביטול
            </button>
          </div>
        </form>
      </div>
  );
};

export default AddEditMedicalCase;
